// Copyright © 2021 Move Closer

import { IConnector, Injectable, IResponse, Payload } from '@movecloser/front-core'

import { IIdentityService } from '@service/identity'
import { INavigationService } from '@service/navigation'
import { ISiteService, SiteData } from '@service/site/site.contracts'

import { ISetupService } from './setup.contracts'
import { SetupFailed } from './setup.exceptions'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Injectable()
export class SetupService implements ISetupService {
  protected readonly connector: IConnector
  protected readonly identity: IIdentityService
  protected readonly navigations: INavigationService
  protected readonly sites: ISiteService

  // private Sentry: ISentry | null = null

  public constructor (
    connector: IConnector,
    identity: IIdentityService,
    navigations: INavigationService,
    sites: ISiteService
  ) {
    this.connector = connector
    this.identity = identity
    this.navigations = navigations
    this.sites = sites
  }

  /**
   * Return locale of current site.
   */
  public getLocale (): string {
    return `${this.sites.getActiveSite().locale}`.toLowerCase()
  }

  private makeId (length: number): string {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    let counter = 0
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
      counter += 1
    }
    return result
  }

  /**
   * Setup application due to API response.
   */
  public async initApp (entryUrl: string, isClient: boolean): Promise<void> {
    isClient ? await this.initClientApp(entryUrl) : await this.initSSRApp(entryUrl)
  }

  /**
   * Determine of restrictions should be turned on.
   */
  public isMaintenance (): boolean {
    return this.sites.getActiveSite().isMaintenance()
  }

  /**
   * Init SSR app with all required setup.
   * @protected
   */
  protected async initSSRApp (entryUrl: string): Promise<void> {
    const data = await this.callApi(entryUrl)
    this.setBasicServices(data)
  }

  /**
   * Init Client app with all required setup.
   * @protected
   */
  protected async initClientApp (entryUrl: string): Promise<void> {
    try {
      const data = await this.callApi(entryUrl)
      this.setBasicServices(data)
      if (typeof document !== 'undefined') {
        this.sites.setAbTestsConfig(data.abTests)
      }
      this.identity.generateSignature()
    } catch (e) {
      if (e instanceof SetupFailed) {
        throw e
      }
    }
  }

  private async callApi (entryUrl: string, retriesLeft: number = 2): Promise<Payload> {
    const token = this.makeId(8) ?? null
    const response: IResponse = await this.connector.call('setup', 'init', {}, {
      referer: entryUrl,
      tokenCheck: token
    })

    if (!response.isSuccessful() || response.status === 0) {
      // if (this.Sentry) {
      //   this.Sentry.addBreadcrumb({
      //     category: 'setup',
      //     type: 'error',
      //     message: response.errors?.message,
      //     level: this.Sentry.Severity.Error,
      //     data: {
      //       ...response,
      //       entryUrl
      //     }
      //   })
      // }

      throw new SetupFailed(
        response.errors?.message || 'Unknown error',
        response.status
      )
    } else {
      // if (this.Sentry) {
      //   this.Sentry.addBreadcrumb({
      //     category: 'setup',
      //     type: 'debug',
      //     message: '[SetupService] initCall result',
      //     level: this.Sentry.Severity.Debug,
      //     data: {
      //       ...response,
      //       entryUrl
      //     }
      //   })
      // }

      // temporary maybe-fix for site mismatch
      if (!this.validateSiteResponse(entryUrl, token, response.data, 3 - retriesLeft) && retriesLeft > 0) {
        return this.callApi(entryUrl, retriesLeft - 1)
      }
    }

    return response.data
  }

  private validateSiteResponse (entryUrl: string, token: string, data: Payload, retryNumber: number) {
    const activeSiteId = data.currentSite
    // const responseToken = data.tokenCheck ?? null
    const sites = (Object.values(data.sites) as SiteData[]).map(site => ({
      id: site.id,
      name: site.name,
      address: site.address
    }))
    const activeSite = sites.find(site => site.id === activeSiteId)

    if (!activeSite) {
      // if (this.Sentry) {
      //   this.Sentry.captureException(
      //     new Error(`[SetupService]: Site mismatch: requested ${entryUrl}, got ${activeSiteId}, not found in sites. Retry number: ${retryNumber}`),
      //     {
      //       extra: {
      //         entryUrl,
      //         activeSiteId,
      //         sites,
      //         retryNumber,
      //         token,
      //         responseToken
      //       }
      //     }
      //   )
      // }

      return false
    }

    const isValid = entryUrl.includes(activeSite.address.replace('http://', '').replace('https://', ''))

    // if (this.Sentry && !isValid) {
    //   this.Sentry.captureException(
    //     new Error(`[SetupService]: Site mismatch: requested ${entryUrl}, got ${activeSite.name} [${activeSite.id}]. Retry number: ${retryNumber}`),
    //     {
    //       extra: {
    //         entryUrl,
    //         activeSiteId,
    //         activeSite,
    //         sites,
    //         retryNumber,
    //         token,
    //         responseToken
    //       }
    //     }
    //   )
    // }

    return isValid
  }

  private setBasicServices (data: Payload): void {
    this.navigations.parseNavigations(data.navs)
    this.navigations.setSubscriptionButtonState(data.hideSubscriptionButton)
    this.navigations.setPremiumButtonLink(data.premiumLink)
    this.sites.setSites(data.sites, data.searchLabels)
    this.sites.setActive(data.currentSite)
    this.sites.setFaqUrl(data.faqUrl)
    this.sites.setAdsConfig(data.adsConfig)
    this.sites.setArticlePrices(data.articlePrices)
    this.sites.setPromoCodesConfig(data.promoCodes)
    this.sites.setWelcomeFormConfig(data.welcomeForm)
    this.sites.setSiteImage(data.siteImage)
  }
}
